/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from "@apollo/client";
import { Box } from "@mui/material";
import React, { useContext, useMemo, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import ConfigurationContext from "../../../context/Configuration";
import UserContext from "../../../context/User";
import { GET_DELIVERY_FEE } from "../../../apollo/server";
import { cateringItemIds, cateringSettingsOf, isCateringCart } from "../../../utils/catering";
import { useOrderMode, ORDER_MODES } from "../../../context/OrderMode";
import { C, FONT_BODY, FOCUS_RING, RADIUS, TYPE } from "../../../utils/designTokens";

const DELIVERY_FEE_QUERY = gql`${GET_DELIVERY_FEE}`;

/**
 * Pricing breakdown + checkout button. Every calculation (calculatePrice,
 * taxCalculation, calculateTotal, delivery zone lookup) is as before, except
 * that a pickup order carries no delivery fee. `note` is the small line under
 * the button ("Welcome deal ($10 off) can be applied at checkout").
 */
function PricingView(props) {
  const configuration = useContext(ConfigurationContext);
  const { cart } = useContext(UserContext);
  const { restaurantData, note } = props;
  const currency = configuration?.currencySymbol || "$";
  const { orderMode } = useOrderMode() || {};
  const isPickup = orderMode === ORDER_MODES.PICKUP;

  const [deliveryCharges, setDeliveryCharges] = useState(0);
  const [deliveryAvailable, setDeliveryAvailable] = useState(true);
  const [noZonesConfigured, setNoZonesConfigured] = useState(false);

  // A catering cart (backend migration 131) is quoted on catering terms at the
  // earliest time it can be wanted; checkout re-quotes at the time picked.
  const cateringIds = useMemo(() => cateringItemIds(restaurantData), [restaurantData]);
  const cateringCart = isCateringCart(cart, cateringIds);
  const cateringPickupOnly =
    cateringCart && cateringSettingsOf(restaurantData).deliveryMode === "PICKUP_ONLY";

  const [fetchDeliveryFee] = useLazyQuery(DELIVERY_FEE_QUERY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const result = data?.getDeliveryFee;
      if (result?.available) {
        setDeliveryCharges(parseFloat(result.fee) || 0);
        setDeliveryAvailable(true);
        setNoZonesConfigured(false);
      } else {
        setDeliveryCharges(0);
        setDeliveryAvailable(false);
        setNoZonesConfigured(result?.noZonesConfigured === true);
      }
    },
    onError: () => {
      setDeliveryCharges(0);
      setDeliveryAvailable(false);
      setNoZonesConfigured(false);
    },
  });

  useEffect(() => {
    if (!restaurantData?._id) return;
    const locationStr = localStorage.getItem("location");
    if (!locationStr) return;
    const loc = JSON.parse(locationStr);
    if (!loc?.latitude || !loc?.longitude) return;
    const lead = cateringCart ? cateringSettingsOf(restaurantData).leadTimeMinutes : 0;
    fetchDeliveryFee({
      variables: {
        restaurantId: restaurantData._id,
        latitude: parseFloat(loc.latitude),
        longitude: parseFloat(loc.longitude),
        ...(cateringCart
          ? { catering: true, at: new Date(Date.now() + lead * 60000).toISOString() }
          : {}),
        // A food-truck stop's cart is quoted from that stop.
        stopId: restaurantData?.orderingStop?.stop?._id || null,
      },
    });
  }, [restaurantData?._id, restaurantData?.orderingStop?.stop?._id, cateringCart]);

  const calculatePrice = useMemo(
    () => (amount = 0) => {
      let itemTotal = 0;
      cart.forEach((cartItem) => {
        const price = parseFloat(cartItem.price);
        if (!isNaN(price)) itemTotal += price * cartItem.quantity;
      });
      const deliveryAmount = amount > 0 ? deliveryCharges : 0;
      return (itemTotal + deliveryAmount).toFixed(2);
    },
    [deliveryCharges, cart]
  );

  const taxCalculation = useMemo(
    () => () => {
      const tax = restaurantData ? +restaurantData.tax : 0;
      if (tax === 0) return tax.toFixed(2);
      // Tax applies to the item subtotal only — pass 0 so the delivery fee is excluded.
      const amount = +calculatePrice(0);
      return ((amount / 100) * tax).toFixed(2);
    },
    [restaurantData, cart, deliveryCharges]
  );

  const calculateTotal = useMemo(
    () => () => {
      let total = 0;
      // Pickup: no delivery fee (calculatePrice only adds it for amount > 0).
      total += +calculatePrice(isPickup ? 0 : deliveryCharges);
      total += +taxCalculation();
      return parseFloat(total).toFixed(2);
    },
    [deliveryCharges, cart, isPickup]
  );

  // Advanced-menu start/end times are stored by the admin as an optional,
  // informational label only — every menu is saved as menuType 'ALL_DAY' and the
  // storefront intentionally shows all menus regardless of those hours
  // (see RestaurantDetail.js: isMenuAvailableNow = () => true). Checkout therefore
  // must NOT be blocked by these times; doing so wrongly stranded carts whenever a
  // menu had hours set. Kept as an always-empty list so the warning/disabled-button
  // branches below remain wired but inert.
  const unavailableMenuNames = useMemo(() => [], []);
  const checkoutBlocked = unavailableMenuNames.length > 0;

  const row = (label, value, opts = {}) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, py: 0.5 }}>
      <Box component="dt" sx={{ ...TYPE.body, color: opts.bold ? C.ink : C.muted, fontWeight: opts.bold ? 700 : 500, fontSize: opts.bold ? 16 : 14 }}>
        {label}
      </Box>
      <Box
        component="dd"
        sx={{
          m: 0,
          ...TYPE.body,
          fontSize: opts.bold ? 16 : 14,
          fontWeight: opts.bold ? 700 : 500,
          color: opts.danger ? C.redLink : C.ink,
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
        }}
      >
        {value}
      </Box>
    </Box>
  );

  const total = calculateTotal();
  const deliveryValue = isPickup
    ? "Free"
    : cateringPickupOnly
      ? "Pickup only"
      : deliveryAvailable
        ? `${currency}${deliveryCharges.toFixed(2)}`
        : noZonesConfigured ? "Not delivering right now" : "Outside delivery area";

  const buttonSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1.5,
    width: "100%",
    height: 56,
    boxSizing: "border-box",
    pl: 2.5,
    pr: 1,
    borderRadius: RADIUS.controlLg,
    fontFamily: FONT_BODY,
    fontSize: 16,
    fontWeight: 700,
    textDecoration: "none",
  };

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${C.border}` }}>
      <Box component="dl" sx={{ m: 0 }}>
        {row("Subtotal", `${currency}${calculatePrice(0)}`)}
        {row("Delivery fee", deliveryValue, { danger: !isPickup && !deliveryAvailable && !cateringPickupOnly })}
        {row("Tax", `${currency}${taxCalculation()}`)}
        <Box sx={{ borderTop: `1px solid ${C.borderStrong}`, mt: 1, pt: 1 }}>
          {row("Total", `${currency}${total}`, { bold: true })}
        </Box>
      </Box>

      {checkoutBlocked && (
        <Box sx={{ mt: 1.5, p: 1.25, borderRadius: RADIUS.control, bgcolor: C.redSoft, ...TYPE.meta, color: C.redLink }}>
          {`${unavailableMenuNames.join(', ')} is not available right now. Please remove these items or come back during the menu's serving hours.`}
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        {checkoutBlocked ? (
          <Box component="button" type="button" disabled sx={{ ...buttonSx, justifyContent: "center", border: 0, bgcolor: C.disabledBg, color: C.disabledText }}>
            Menu unavailable
          </Box>
        ) : (
          <Box
            component={RouterLink}
            to="/checkout"
            sx={{
              ...buttonSx,
              bgcolor: C.red,
              color: "#FFFFFF",
              transition: "background-color .15s ease",
              "&:hover": { bgcolor: C.redHover },
              ...FOCUS_RING,
            }}
          >
            <span>Go to checkout</span>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                height: 40,
                px: 1.75,
                borderRadius: RADIUS.control,
                bgcolor: C.redDark,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {`${currency}${total}`}
            </Box>
          </Box>
        )}
      </Box>
      {note && (
        <Box component="p" sx={{ ...TYPE.meta, m: 0, mt: 1.25, color: C.muted, textAlign: "center" }}>
          {note}
        </Box>
      )}
    </Box>
  );
}

export default React.memo(PricingView);
